import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, MapPin, Clock, ArrowRight, Search, BadgeCheck,
  ChevronDown, ChevronLeft, ChevronRight, Loader2, Copy, Check,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PublicLayout, { PageHero } from '../components/public/PublicLayout';
import { EASE, Reveal } from '../components/public/motion';
import { supabase } from '../lib/supabaseClient';

const PAGE_SIZE = 6;

const candidateRef = (uuid) => {
  if (!uuid) return '0000';
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    hash = ((hash << 5) - hash) + uuid.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36).toUpperCase().substring(0, 4).padStart(4, '0');
};

const ROLE_TYPE_OPTIONS = [
  'All roles',
  'Software Engineering',
  'Data & AI',
  'Cloud & DevOps',
  'Cybersecurity',
  'Project Delivery',
  'Architecture',
  'Business Analysis & UX',
  'QA & Testing',
  'Infrastructure',
];

const CLEARANCE_COLORS = {
  TSPV: 'bg-purple-100 text-purple-700 border-purple-200',
  NV2: 'bg-blue-100 text-blue-700 border-blue-200',
  NV1: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  Baseline: 'bg-slate-100 text-slate-700 border-slate-200',
  None: 'bg-slate-50 text-slate-500 border-slate-100',
};

const SENIORITY_COLORS = {
  Junior: 'bg-green-50 text-green-700 border-green-200',
  'Mid career': 'bg-teal-50 text-teal-700 border-teal-200',
  Senior: 'bg-blue-50 text-blue-700 border-blue-200',
  Lead: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Management: 'bg-amber-50 text-amber-700 border-amber-200',
  Executive: 'bg-rose-50 text-rose-700 border-rose-200',
};

const CandidateCard = ({ candidate }) => {
  const [copied, setCopied] = useState(false);
  const hideTimer = useRef(null);

  const scheduleHide = (ms) => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setCopied(false), ms);
  };

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  const handleRequest = () => {
    const ref = candidateRef(candidate.candidate_id);
    const draft = `Hi Appsytec,\n\nI'm interested in finding out more about the ${candidate.display_role} candidate (Ref: Candidate-${ref}).\n\nPlease send through their full profile and any available references.\n\nThank you`;
    navigator.clipboard.writeText(draft).then(() => {
      setCopied(true);
      scheduleHide(6000); // mobile fallback — auto-hides after 6s
    });
  };

  return (
    <motion.article
      layout
      initial={{ opacity: 0, scale: 0.97, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97, y: -10 }}
      transition={{ duration: 0.35, ease: EASE }}
      whileHover={{ y: -5 }}
      onMouseEnter={() => { if (copied) scheduleHide(8000); }}
      onMouseLeave={() => { if (copied) scheduleHide(2500); }}
      className="group flex flex-col rounded-3xl border border-gray-200 bg-white p-7 transition-shadow duration-300 hover:shadow-xl hover:shadow-gray-900/5 md:p-8"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest text-red-500">{candidate.role_type}</p>
          <h3 className="mt-1 font-display text-2xl font-bold text-gray-900">{candidate.display_role}</h3>
        </div>
        {candidate.seniority && (
          <span className={`mt-1 shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${SENIORITY_COLORS[candidate.seniority] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
            {candidate.seniority}
          </span>
        )}
      </div>

      {/* Pitch */}
      <p className="mt-5 text-base leading-relaxed text-gray-600">{candidate.pitch}</p>

      {/* Metadata row */}
      <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
        {candidate.display_clearance && (
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${CLEARANCE_COLORS[candidate.display_clearance] || CLEARANCE_COLORS.None}`}>
            <Shield className="h-3.5 w-3.5" />
            {candidate.display_clearance === 'None' ? 'No clearance' : candidate.display_clearance}
          </span>
        )}
        {candidate.display_location && (
          <span className="inline-flex items-center gap-1.5 text-gray-500">
            <MapPin className="h-3.5 w-3.5 text-gray-400" />
            {candidate.display_location}
          </span>
        )}
        {candidate.display_notice && (
          <span className={`inline-flex items-center gap-1.5 ${candidate.display_notice.toLowerCase().includes('available') ? 'font-semibold text-green-600' : 'text-gray-500'}`}>
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            {candidate.display_notice}
          </span>
        )}
      </div>

      {/* Skills */}
      {candidate.display_skills && candidate.display_skills.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {candidate.display_skills.map((skill) => (
            <span key={skill} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
              {skill}
            </span>
          ))}
        </div>
      )}

      {/* CTA */}
      <div className="mt-auto pt-7 space-y-2">
        <button
          onClick={handleRequest}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-full border-2 px-5 py-3 text-sm font-semibold transition-all duration-300 ${
            copied
              ? 'border-green-500 bg-green-500 text-white'
              : 'border-gray-900 text-gray-900 hover:border-red-500 hover:bg-red-500 hover:text-white'
          }`}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" /> Copied to clipboard!
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" /> Request this candidate
            </>
          )}
        </button>
        {copied && (
          <div className="rounded-2xl border border-green-100 bg-green-50 p-4 text-xs text-gray-600 space-y-2">
            <p className="italic leading-relaxed">
              "Hi Appsytec, I'm interested in finding out more about the <strong>{candidate.display_role}</strong> candidate (Ref: Candidate-{candidateRef(candidate.candidate_id)}). Please send through their full profile and any available references."
            </p>
            <p className="text-gray-500">
              Paste this into an email to{' '}
              <a href="mailto:contact@appsytec.com" className="font-semibold text-red-500 hover:underline">
                contact@appsytec.com
              </a>{' '}
              and we'll get back to you with their details.
            </p>
          </div>
        )}
      </div>
    </motion.article>
  );
};

const CandidatesPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [roleType, setRoleType] = useState('All roles');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setPageLoading(true);
      setFetchError(null);
      try {
        const { data, error } = await supabase
          .from('candidate_public_profiles')
          .select('*')
          .eq('is_public', true)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        if (!cancelled) setCandidates(data || []);
      } catch (err) {
        if (!cancelled) setFetchError('Could not load candidates. Please try again later.');
        console.error('CandidatesPage fetch error:', err);
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return candidates.filter((c) => {
      if (roleType !== 'All roles' && c.role_type !== roleType) return false;
      if (!query) return true;
      const haystack = [
        c.display_role,
        c.role_type,
        c.display_location,
        c.seniority,
        c.display_clearance,
        c.pitch,
        ...(c.display_skills || []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [candidates, roleType, search]);

  const isFiltered = roleType !== 'All roles' || search.trim() !== '';

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <PublicLayout>
      <PageHero
        eyebrow="Available now"
        title="Talent ready to deploy"
        subtitle="A curated showcase of vetted ICT specialists currently looking and available for work. Request full profiles and references through our team."
      >
        <Reveal delay={0.35}>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-gray-300 backdrop-blur-sm">
            <BadgeCheck className="h-4 w-4 text-red-400" />
            All candidates pre-vetted and reference-checked
          </div>
        </Reveal>
      </PageHero>

      <section className="bg-gray-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">

          {/* Loading state */}
          {pageLoading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin text-red-500" />
              <p className="text-sm">Loading available candidates…</p>
            </div>
          ) : fetchError ? (
            <div className="rounded-3xl border border-dashed border-red-200 bg-white p-12 text-center">
              <p className="font-display text-xl font-bold text-gray-900">Something went wrong</p>
              <p className="mt-2 text-gray-500">{fetchError}</p>
            </div>
          ) : (
            <>
              {/* Filter bar */}
              <Reveal>
                <div className="flex flex-col gap-4 rounded-3xl border border-gray-200 bg-white p-5 shadow-lg shadow-gray-900/5 md:flex-row md:items-center md:p-6">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                      placeholder="Search by role, skill, location or keyword…"
                      className="w-full rounded-full border border-gray-200 bg-gray-50 py-2.5 pl-11 pr-4 text-sm transition-all duration-200 focus:border-transparent focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  {/* Role type dropdown */}
                  <div className="relative shrink-0">
                    <select
                      value={roleType}
                      onChange={(e) => { setRoleType(e.target.value); setPage(1); }}
                      className="w-full appearance-none rounded-full border border-gray-200 bg-white py-2.5 pl-4 pr-10 text-sm font-medium text-gray-700 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-red-500 md:w-52"
                    >
                      {ROLE_TYPE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>

                  {/* Count + clear + pagination */}
                  <div className="flex shrink-0 items-center gap-3 text-sm text-gray-500">
                    <span>
                      <strong className="text-gray-900">{filtered.length}</strong>
                      {' '}candidate{filtered.length !== 1 ? 's' : ''}
                    </span>
                    {isFiltered && (
                      <button
                        onClick={() => { setRoleType('All roles'); setSearch(''); setPage(1); }}
                        className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        Clear
                      </button>
                    )}
                    {totalPages > 1 && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30"
                          aria-label="Previous page"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30"
                          aria-label="Next page"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>

              {/* Results grid */}
              {candidates.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-10 rounded-3xl border border-dashed border-gray-300 bg-white p-12 text-center"
                >
                  <p className="font-display text-xl font-bold text-gray-900">No candidates listed yet</p>
                  <p className="mt-2 text-gray-500">
                    Check back soon, or tell us what you need and we'll respond with a tailored shortlist.
                  </p>
                  <div className="mt-6">
                    <Link
                      to="/contact"
                      className="rounded-full bg-red-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600"
                    >
                      Request a custom shortlist
                    </Link>
                  </div>
                </motion.div>
              ) : (
                <>
                  <motion.div layout className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <AnimatePresence mode="popLayout">
                      {paginated.map((candidate) => (
                        <CandidateCard key={candidate.id} candidate={candidate} />
                      ))}
                    </AnimatePresence>
                  </motion.div>

                  {filtered.length === 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-10 rounded-3xl border border-dashed border-gray-300 bg-white p-12 text-center"
                    >
                      <p className="font-display text-xl font-bold text-gray-900">No candidates match those filters</p>
                      <p className="mt-2 text-gray-500">
                        Try a different role type, or tell us what you need and we'll source it.
                      </p>
                      <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                        <button
                          onClick={() => { setRoleType('All roles'); setSearch(''); }}
                          className="rounded-full border-2 border-gray-900 px-6 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-900 hover:text-white"
                        >
                          Reset filters
                        </button>
                        <Link
                          to="/contact"
                          className="rounded-full bg-red-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600"
                        >
                          Request a custom shortlist
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </>
              )}

              {/* Bottom CTA */}
              <Reveal delay={0.1} className="mt-16">
                <div className="relative overflow-hidden rounded-3xl bg-gray-950 p-8 text-center text-white md:p-12">
                  <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-red-500/20 blur-3xl" />
                  <h2 className="relative font-display text-2xl font-extrabold tracking-tight md:text-4xl">
                    Can't see the right fit?
                  </h2>
                  <p className="relative mx-auto mt-3 max-w-xl text-gray-400">
                    This is only a snapshot of our talent pool. Tell us about your role and we'll respond with a
                    tailored shortlist of available, cleared specialists.
                  </p>
                  <Link
                    to="/contact"
                    className="group relative mt-7 inline-flex items-center gap-2 rounded-full bg-red-500 px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/25"
                  >
                    Request a shortlist
                    <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default CandidatesPage;
